import { useState, useMemo } from "react";
import { format } from "date-fns";
import { Search, X, Settings, FileSpreadsheet, Phone, Mail, Info, Plus } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useCustomerPromises } from "@/hooks/useCustomerPromises";
import { usePromiseColumnPreferences } from "@/hooks/usePromiseColumnPreferences";
import { useCreatePromise, type CreatePromiseRequest } from "@/hooks/usePromisesData";
import { ColumnConfigDialog } from "@/components/collections/column-config-dialog";
import { PromiseDetailsDrawer } from "@/components/collections/promise-details-drawer";
import { CreatePromiseDrawer, type PromiseFormData } from "@/components/collections/create-promise-drawer";
import { CaseDetailsDialog } from "@/components/collections/case-details-dialog";
import { getGlobalDataStore } from "@/lib/mock-data/global-data-store";
import { exportPromisesToExcel } from "@/lib/promise-export-utils";
import { toast } from "sonner";
import type { PromiseToPay, CollectionCase } from "@/types/collections";
import { formatCurrency } from "@/lib/utils/formatters";

interface PromisesTabProps {
  customerId: string;
  customerName: string;
  accountNumber?: string;
}

export function PromisesTab({ customerId, customerName, accountNumber }: PromisesTabProps) {
  const { promises: rawPromises, isLoading } = useCustomerPromises(customerId);
  
  // Create promise mutation (mode-aware - calls API in live mode, returns mock in demo mode)
  const createPromiseMutation = useCreatePromise();
  
  // State
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [selectedPromise, setSelectedPromise] = useState<PromiseToPay | null>(null);
  const [detailSheetOpen, setDetailSheetOpen] = useState(false);
  const [createPromiseModalOpen, setCreatePromiseModalOpen] = useState(false);
  const [selectedCaseForDetails, setSelectedCaseForDetails] = useState<CollectionCase | null>(null);
  const [caseDetailsOpen, setCaseDetailsOpen] = useState(false);
  const [columnConfigOpen, setColumnConfigOpen] = useState(false);
  
  // Column preferences
  const { columns, savePreferences, resetToDefaults } = usePromiseColumnPreferences();
  
  // Auto-update promises with past promise dates to 'Broken' status
  const processedPromises = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return rawPromises.map(promise => {
      const promiseDate = new Date(promise.promiseDate);
      promiseDate.setHours(0, 0, 0, 0);

      if (promiseDate < today && promise.status === 'Active') {
        const daysOverdue = Math.ceil((today.getTime() - promiseDate.getTime()) / (1000 * 60 * 60 * 24));
        return {
          ...promise,
          status: 'Broken' as const,
          daysOverdue,
        };
      }

      return promise;
    });
  }, [rawPromises]);
  
  // Filter promises based on status filter and search
  const filteredPromises = useMemo(() => {
    let filtered = processedPromises;

    // Filter by status dropdown
    if (statusFilter !== 'all') {
      filtered = filtered.filter(p => p.status === statusFilter);
    }

    // Filter by search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(p =>
        p.customerName.toLowerCase().includes(query) ||
        p.customerAccountNumber.toLowerCase().includes(query) ||
        p.promisedAmount.toString().includes(query) ||
        p.id.toLowerCase().includes(query)
      );
    }

    // Sort by Promise Date in ascending order (oldest first)
    filtered = [...filtered].sort((a, b) => a.promiseDate.getTime() - b.promiseDate.getTime());

    return filtered;
  }, [processedPromises, statusFilter, searchQuery]);
  
  // Get visible columns in order
  const visibleColumns = useMemo(() => {
    return columns
      .filter(col => col.visible)
      .sort((a, b) => a.order - b.order);
  }, [columns]);
  
  // Selection handlers
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedRows(new Set(filteredPromises.map(p => p.id)));
    } else {
      setSelectedRows(new Set());
    }
  };
  
  const handleSelectRow = (id: string, checked: boolean) => {
    const newSelected = new Set(selectedRows);
    if (checked) {
      newSelected.add(id);
    } else {
      newSelected.delete(id);
    }
    setSelectedRows(newSelected);
  };
  
  const isAllSelected = filteredPromises.length > 0 && selectedRows.size === filteredPromises.length;
  const isSomeSelected = selectedRows.size > 0 && selectedRows.size < filteredPromises.length;
  
  // Action handlers
  const handleViewDetails = (promise: PromiseToPay) => {
    setSelectedPromise(promise);
    setDetailSheetOpen(true);
  };
  
  const handleCaseNumberClick = (caseNumber: string) => {
    const dataStore = getGlobalDataStore();
    const allCases = dataStore.getAllCases();
    const caseData = allCases.find(c => c.caseId === caseNumber);
    
    if (caseData) {
      setSelectedCaseForDetails(caseData);
      setCaseDetailsOpen(true);
    } else {
      toast.error('Case not found');
    }
  };
  
  const handleCreateNewPromise = (data: PromiseFormData) => {
    // Map form data to API request format
    const request: CreatePromiseRequest = {
      customerId: parseInt(data.customer.id, 10),
      promiseDate: format(data.promiseDate, 'yyyy-MM-dd'),
      promisedAmount: data.promisedAmount,
      confirmationMethod: data.confirmationMethod.toLowerCase(),
      notes: data.notes,
    };

    createPromiseMutation.mutate(request, {
      onSuccess: () => {
        toast.success('New promise created successfully');
        setCreatePromiseModalOpen(false);
      },
      onError: (error) => {
        toast.error(`Failed to create promise: ${error.message}`);
      },
    });
  };
  
  const handleExportToExcel = () => {
    try {
      exportPromisesToExcel(filteredPromises);
      toast.success(`Exported ${filteredPromises.length} promise(s) to Excel`);
    } catch (error) {
      toast.error('Failed to export promises');
      console.error('Export error:', error);
    }
  };
  
  const handleExportAllColumns = () => {
    try {
      exportPromisesToExcel(filteredPromises, `customer-${customerId}-promises-all-columns-${new Date().toISOString().split('T')[0]}.csv`);
      toast.success(`Exported ${filteredPromises.length} promise(s) with all columns to Excel`);
    } catch (error) {
      toast.error('Failed to export promises');
      console.error('Export error:', error);
    }
  };
  
  const handleBulkEmail = () => {
    if (selectedRows.size === 1) {
      const promiseId = Array.from(selectedRows)[0];
      const promise = processedPromises.find(p => p.id === promiseId);
      if (promise) {
        toast.info(`Opening email for ${promise.customerName}`);
      }
    } else {
      toast.success(`Email sent to ${selectedRows.size} customers`);
      setSelectedRows(new Set());
    }
  };
  
  const handleBulkCall = () => {
    if (selectedRows.size === 1) {
      const promiseId = Array.from(selectedRows)[0];
      const promise = processedPromises.find(p => p.id === promiseId);
      if (promise) {
        toast.info(`Logging call for ${promise.customerName}`);
      }
    } else {
      toast.info(`Logging calls for ${selectedRows.size} customers`);
    }
  };
  
  const handleClearSelection = () => {
    setSelectedRows(new Set());
  };
  
  // Badge helpers
  const getStatusBadge = (status: PromiseToPay['status']) => {
    const variants: Record<PromiseToPay['status'], { className: string }> = {
      Active: { className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' },
      Broken: { className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' },
      Paid: { className: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' },
      'Partially Paid': { className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400' },
    };
    return <Badge className={variants[status].className}>{status}</Badge>;
  };
  
  const getConfirmationMethodBadge = (method: PromiseToPay['confirmationMethod']) => {
    const icons = {
      Phone: <Phone className="h-3 w-3 mr-1" />,
      Email: <Mail className="h-3 w-3 mr-1" />,
      Portal: <Info className="h-3 w-3 mr-1" />,
    };
    return (
      <Badge variant="outline" className="flex items-center gap-1">
        {icons[method]}
        {method}
      </Badge>
    );
  };
  
  // Helper function to render table cell content based on column ID
  const renderTableCell = (columnId: string, promise: PromiseToPay) => {
    switch (columnId) {
      case 'select':
        return (
          <Checkbox
            checked={selectedRows.has(promise.id)}
            onCheckedChange={(checked) => handleSelectRow(promise.id, checked as boolean)}
            aria-label={`Select promise ${promise.id}`}
          />
        );
      case 'promiseId':
        return (
          <button
            onClick={() => handleViewDetails(promise)}
            className="text-primary hover:underline hover:text-primary/80 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded cursor-pointer transition-colors"
          >
            {promise.id}
          </button>
        );
      case 'customerName':
        return (
          <button
            onClick={() => handleViewDetails(promise)}
            className="text-left hover:underline text-primary font-medium focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded"
          >
            {promise.customerName}
          </button>
        );
      case 'customerAccountNumber':
        return <span>{promise.customerAccountNumber}</span>;
      case 'caseNumber':
        return promise.caseNumber ? (
          <button
            onClick={() => handleCaseNumberClick(promise.caseNumber)}
            className="text-primary hover:underline hover:text-primary/80 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded cursor-pointer transition-colors font-medium"
          >
            {promise.caseNumber}
          </button>
        ) : (
          <span className="text-muted-foreground">-</span>
        );
      case 'lastAction':
        return promise.lastAction ? (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button className="text-muted-foreground hover:text-primary transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded">
                  <Info className="h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <div className="space-y-1">
                  <p className="font-semibold text-sm capitalize">{promise.lastAction.type.replace('_', ' ')}</p>
                  <p className="text-xs">{promise.lastAction.description}</p>
                  <div className="flex items-center justify-between text-xs text-muted-foreground pt-1 border-t">
                    <span>{promise.lastAction.performedBy}</span>
                    <span>{format(promise.lastAction.date, 'dd/MM/yyyy')}</span>
                  </div>
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <span className="text-muted-foreground">-</span>
        );
      case 'promiseDate':
        return <span>{format(promise.promiseDate, 'dd/MM/yyyy')}</span>;
      case 'daysOverdue':
        return promise.daysOverdue && promise.daysOverdue > 0 ? (
          <span className="text-red-600 font-medium">{promise.daysOverdue}</span>
        ) : (
          <span className="text-muted-foreground">-</span>
        );
      case 'amount':
        return <div className="font-medium">{formatCurrency(promise.promisedAmount)}</div>;
      case 'transactions':
        return <div className="text-sm">{promise.invoices.length}</div>;
      case 'status':
        return getStatusBadge(promise.status);
      case 'method':
        return getConfirmationMethodBadge(promise.confirmationMethod);
      case 'customerEmail':
        return <span className="text-sm">{promise.customerEmail || '-'}</span>;
      case 'customerPhone':
        return <span className="text-sm">{promise.customerPhone || '-'}</span>;
      case 'createdDate':
        return <span className="text-sm">{promise.createdAt ? format(new Date(promise.createdAt), 'dd/MM/yyyy') : '-'}</span>;
      case 'createdBy':
        return <span className="text-sm">{promise.createdBy || '-'}</span>;
      case 'notes':
        return <span className="text-sm truncate max-w-xs">{promise.notes || '-'}</span>;
      default:
        return null;
    }
  };
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Loading promises...</p>
      </div>
    );
  }
  
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle>Promise to Pay Records ({filteredPromises.length})</CardTitle>
              <CardDescription>
                Payment promises for {customerName}
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button onClick={() => setCreatePromiseModalOpen(true)} className="flex items-center gap-2">
                <Plus className="h-4 w-4" />
                Create Promise
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                  >
                    <FileSpreadsheet className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={handleExportToExcel}>
                    Export Current View
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleExportAllColumns}>
                    Export All Columns
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setColumnConfigOpen(true)}
                      aria-label="Configure Columns"
                    >
                      <Settings className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Configure Columns</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        </CardHeader>
        
        {/* Search Bar and Status Filter */}
        <div className="px-6 pb-4">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search by promise ID, amount..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-9"
              />
              {searchQuery && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSearchQuery("")}
                  className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="Active">Active</SelectItem>
                <SelectItem value="Broken">Broken</SelectItem>
                <SelectItem value="Paid">Paid</SelectItem>
                <SelectItem value="Partially Paid">Partially Paid</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                {visibleColumns.map((column) => (
                  <TableHead 
                    key={column.id}
                    className={column.id === 'select' ? 'w-12' : ''}
                  >
                    {column.id === 'select' ? (
                      <Checkbox
                        checked={isAllSelected}
                        onCheckedChange={handleSelectAll}
                        aria-label="Select all promises"
                        className={isSomeSelected ? "data-[state=checked]:bg-primary/50" : ""}
                      />
                    ) : (
                      column.label
                    )}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredPromises.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={visibleColumns.length} className="text-center text-muted-foreground py-8">
                    {searchQuery || statusFilter !== 'all' 
                      ? 'No promises found matching your criteria'
                      : 'No promises recorded for this customer'}
                  </TableCell>
                </TableRow>
              ) : (
                filteredPromises.map((promise) => {
                  const isSelected = selectedRows.has(promise.id);
                  return (
                    <TableRow 
                      key={promise.id}
                      className={isSelected ? "bg-muted/50" : ""}
                    >
                      {visibleColumns.map((column) => (
                        <TableCell key={column.id}>
                          {renderTableCell(column.id, promise)}
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      
      {/* Promise Details Drawer */}
      <PromiseDetailsDrawer
        promise={selectedPromise}
        open={detailSheetOpen}
        onOpenChange={setDetailSheetOpen}
      />
      
      {/* Create New Promise Drawer */}
      <CreatePromiseDrawer
        open={createPromiseModalOpen}
        onOpenChange={setCreatePromiseModalOpen}
        onSave={handleCreateNewPromise}
        prePopulatedCustomer={accountNumber ? {
          customerId,
          customerName,
          accountNumber,
        } : undefined}
      />
      
      {/* Case Details Dialog */}
      <CaseDetailsDialog
        open={caseDetailsOpen}
        onOpenChange={setCaseDetailsOpen}
        caseData={selectedCaseForDetails}
      />
      
      {/* Column Configuration Dialog */}
      <ColumnConfigDialog
        open={columnConfigOpen}
        onOpenChange={setColumnConfigOpen}
        columns={columns}
        onSave={savePreferences}
        onReset={resetToDefaults}
      />
      
      {/* Floating Actions Bar */}
      {selectedRows.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 animate-in slide-in-from-bottom-5 duration-300">
          <div className="bg-primary text-primary-foreground rounded-lg shadow-2xl border border-primary/20">
            <div className="flex items-center gap-4 px-6 py-4">
              {/* Selection Info */}
              <div className="flex items-center gap-3 border-r border-primary-foreground/20 pr-4">
                <div className="flex flex-col">
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="bg-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground/30">
                      {selectedRows.size}
                    </Badge>
                    <span className="font-medium">
                      {selectedRows.size === 1 ? "promise" : "promises"} selected
                    </span>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleBulkEmail}
                  className="gap-2"
                >
                  <Mail className="h-4 w-4" />
                  Send Email
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleBulkCall}
                  className="gap-2"
                >
                  <Phone className="h-4 w-4" />
                  Log Call
                </Button>

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClearSelection}
                  className="gap-2 hover:bg-primary-foreground/20"
                >
                  <X className="h-4 w-4" />
                  Clear
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
